import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { API } from "@/App";
import Layout from "@/components/Layout";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Slider } from "@/components/ui/slider";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { 
  CalendarIcon, 
  Heart, 
  Sparkles, 
  Target, 
  MessageSquare, 
  CheckCircle2,
  AlertCircle,
  ChevronRight,
  Loader2,
  TrendingUp,
  ClipboardList
} from "lucide-react";
import { format } from "date-fns";
import { cn } from "@/lib/utils";
import { CheckInFullDisplay, ProgressLineChart } from "@/components/CheckInCharts";

// Rating Component with labels
const RatingSlider = ({ label, value, onChange, description }) => {
  const getColor = (val) => {
    if (val <= 3) return "text-red-500";
    if (val <= 5) return "text-amber-500";
    if (val <= 7) return "text-yellow-500";
    return "text-green-500";
  };

  return (
    <div className="space-y-3 py-3 border-b border-slate-100 last:border-0">
      <div className="flex items-center justify-between">
        <div>
          <Label className="text-sm font-medium text-slate-900">{label}</Label>
          {description && (
            <p className="text-xs text-slate-500 mt-0.5">{description}</p>
          )}
        </div>
        <Badge 
          variant="outline" 
          className={cn("text-lg font-bold min-w-[3rem] justify-center", getColor(value))}
        >
          {value}
        </Badge>
      </div>
      <div className="flex items-center gap-4">
        <span className="text-xs text-slate-400 w-6">1</span>
        <Slider
          value={[value]}
          onValueChange={([val]) => onChange(val)}
          min={1}
          max={10}
          step={1}
          className="flex-1"
        />
        <span className="text-xs text-slate-400 w-6">10</span>
      </div>
    </div>
  );
};

// Custom question renderer
const CustomQuestionField = ({ question, value, onChange }) => {
  switch (question.field_type) {
    case "text":
      return (
        <div className="space-y-2">
          <Label className="text-sm font-medium text-slate-900">
            {question.question_text}
            {question.is_required && <span className="text-red-500 ml-1">*</span>}
          </Label>
          {question.help_text && <p className="text-xs text-slate-500">{question.help_text}</p>}
          <Input
            value={value || ""}
            onChange={(e) => onChange(e.target.value)}
            placeholder={question.placeholder || ""}
          />
        </div>
      );
    case "textarea":
      return (
        <div className="space-y-2">
          <Label className="text-sm font-medium text-slate-900">
            {question.question_text}
            {question.is_required && <span className="text-red-500 ml-1">*</span>}
          </Label>
          {question.help_text && <p className="text-xs text-slate-500">{question.help_text}</p>}
          <Textarea
            value={value || ""}
            onChange={(e) => onChange(e.target.value)}
            placeholder={question.placeholder || ""}
            className="min-h-[100px]"
          />
        </div>
      );
    case "number":
      return (
        <div className="space-y-2">
          <Label className="text-sm font-medium text-slate-900">
            {question.question_text}
            {question.is_required && <span className="text-red-500 ml-1">*</span>}
          </Label>
          {question.help_text && <p className="text-xs text-slate-500">{question.help_text}</p>}
          <Input
            type="number"
            value={value || ""}
            onChange={(e) => onChange(e.target.value)}
            placeholder={question.placeholder || ""}
          />
        </div>
      );
    case "rating":
      return (
        <RatingSlider
          label={question.question_text + (question.is_required ? " *" : "")}
          description={question.help_text}
          value={value || 5}
          onChange={onChange}
        />
      );
    case "yes_no":
      return (
        <div className="space-y-2">
          <Label className="text-sm font-medium text-slate-900">
            {question.question_text}
            {question.is_required && <span className="text-red-500 ml-1">*</span>}
          </Label>
          {question.help_text && <p className="text-xs text-slate-500">{question.help_text}</p>}
          <RadioGroup
            value={value === true ? "yes" : value === false ? "no" : undefined}
            onValueChange={(val) => onChange(val === "yes")}
            className="flex gap-4"
          >
            <div className="flex items-center space-x-2 p-3 rounded-lg border border-slate-200 hover:bg-slate-50 cursor-pointer">
              <RadioGroupItem value="yes" id={`q${question.id}-yes`} />
              <Label htmlFor={`q${question.id}-yes`} className="cursor-pointer">Yes</Label>
            </div>
            <div className="flex items-center space-x-2 p-3 rounded-lg border border-slate-200 hover:bg-slate-50 cursor-pointer">
              <RadioGroupItem value="no" id={`q${question.id}-no`} />
              <Label htmlFor={`q${question.id}-no`} className="cursor-pointer">No</Label>
            </div>
          </RadioGroup>
        </div>
      );
    case "multiple_choice":
      return (
        <div className="space-y-2">
          <Label className="text-sm font-medium text-slate-900">
            {question.question_text}
            {question.is_required && <span className="text-red-500 ml-1">*</span>}
          </Label>
          {question.help_text && <p className="text-xs text-slate-500">{question.help_text}</p>}
          <Select value={value || ""} onValueChange={onChange}>
            <SelectTrigger>
              <SelectValue placeholder="Select an option" />
            </SelectTrigger>
            <SelectContent>
              {(question.options?.choices || []).map((choice) => (
                <SelectItem key={choice} value={choice}>{choice}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      );
    case "checkbox":
      return (
        <div className="space-y-2">
          <Label className="text-sm font-medium text-slate-900">
            {question.question_text}
            {question.is_required && <span className="text-red-500 ml-1">*</span>}
          </Label>
          {question.help_text && <p className="text-xs text-slate-500">{question.help_text}</p>}
          <div className="space-y-2">
            {(question.options?.choices || []).map((choice) => (
              <div key={choice} className="flex items-center space-x-2">
                <Checkbox
                  id={`q${question.id}-${choice}`}
                  checked={(value || []).includes(choice)}
                  onCheckedChange={(checked) => {
                    const arr = value || [];
                    onChange(checked ? [...arr, choice] : arr.filter((v) => v !== choice));
                  }}
                />
                <Label htmlFor={`q${question.id}-${choice}`} className="cursor-pointer text-sm">{choice}</Label>
              </div>
            ))}
          </div>
        </div>
      );
    default:
      return (
        <div className="space-y-2">
          <Label>{question.question_text}</Label>
          <Input value={value || ""} onChange={(e) => onChange(e.target.value)} />
        </div>
      );
  }
};

// Helper to get field label/description from overrides
const getFieldProps = (fieldKey, fieldOverrides, defaultLabel, defaultDesc) => {
  const override = fieldOverrides[fieldKey];
  return {
    label: override?.label || defaultLabel,
    description: override?.description || defaultDesc,
  };
};

export default function WeeklyCheckIn({ user }) {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [existingCheckIn, setExistingCheckIn] = useState(null);
  const [allCheckIns, setAllCheckIns] = useState([]);
  const [step, setStep] = useState(1);

  // Field overrides and custom questions
  const [fieldOverrides, setFieldOverrides] = useState({});
  const [customQuestions, setCustomQuestions] = useState([]);
  const hasCustomQuestions = customQuestions.length > 0;
  const totalSteps = hasCustomQuestions ? 6 : 5;

  // Form state
  const [checkInDate, setCheckInDate] = useState(new Date());
  
  // Loved Well section
  const [lovedGod, setLovedGod] = useState(5);
  const [lovedPeople, setLovedPeople] = useState(5);

  // Fruits of Spirit section
  const [love, setLove] = useState(5);
  const [joy, setJoy] = useState(5);
  const [peace, setPeace] = useState(5);
  const [patience, setPatience] = useState(5);
  const [kindness, setKindness] = useState(5);
  const [goodness, setGoodness] = useState(5);
  const [faithfulness, setFaithfulness] = useState(5);
  const [gentleness, setGentleness] = useState(5);
  const [selfControl, setSelfControl] = useState(5);

  // Glorifying God section
  const [useOfWords, setUseOfWords] = useState(5);
  const [thoughtLife, setThoughtLife] = useState(5);
  const [useOfTime, setUseOfTime] = useState(5);
  const [useOfMoney, setUseOfMoney] = useState(5);
  const [managementOfTemptations, setManagementOfTemptations] = useState(5);

  // Reflection questions
  const [biggestFear, setBiggestFear] = useState("");
  const [biggestTemptation, setBiggestTemptation] = useState("");
  const [biggestVictory, setBiggestVictory] = useState("");

  // Honesty check
  const [wasHonest, setWasHonest] = useState(null);
  const [dishonestyReason, setDishonestyReason] = useState("");

  // Custom question responses: { [questionId]: value }
  const [customResponses, setCustomResponses] = useState({});

  useEffect(() => {
    checkStatus();
  }, []);

  const checkStatus = async () => {
    try {
      const [statusRes, historyRes, fieldsRes, questionsRes] = await Promise.all([
        axios.get(`${API}/checkins/status`),
        axios.get(`${API}/checkins?limit=10`),
        axios.get(`${API}/checkin-fields`),
        axios.get(`${API}/custom-questions`),
      ]);
      
      // Store latest check-in for reference but don't block new submissions
      if (statusRes.data.checkin) {
        setExistingCheckIn(statusRes.data.checkin);
      }
      setAllCheckIns(historyRes.data);

      // Build field overrides map
      const overrides = {};
      (fieldsRes.data || []).forEach((f) => {
        overrides[f.field_key] = { label: f.label, description: f.description };
      });
      setFieldOverrides(overrides);

      // Custom questions for this student
      setCustomQuestions(questionsRes.data || []);
    } catch (error) {
      console.error("Error checking status:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async () => {
    if (wasHonest === null) {
      alert("Please answer the honesty question.");
      return;
    }

    if (!wasHonest && !dishonestyReason.trim()) {
      alert("Please explain why you were not fully honest.");
      return;
    }

    // Validate required custom questions
    if (hasCustomQuestions) {
      for (const q of customQuestions) {
        if (q.is_required) {
          const val = customResponses[q.id];
          if (val === undefined || val === null || val === "" || (Array.isArray(val) && val.length === 0)) {
            alert(`Please answer: "${q.question_text}"`);
            setStep(totalSteps === 6 ? 6 : 5);
            return;
          }
        }
      }
    }

    // Build custom_responses array
    const customResponsesArray = hasCustomQuestions
      ? customQuestions.map((q) => ({
          question_id: q.id,
          question_text: q.question_text,
          field_type: q.field_type,
          response_value: customResponses[q.id] ?? null,
        }))
      : null;

    setSubmitting(true);
    try {
      await axios.post(`${API}/checkins`, {
        checkin_date: checkInDate.toISOString(),
        loved_well: {
          loved_god: lovedGod,
          loved_people: lovedPeople
        },
        fruits_of_spirit: {
          love,
          joy,
          peace,
          patience,
          kindness,
          goodness,
          faithfulness,
          gentleness,
          self_control: selfControl
        },
        glorifying_god: {
          use_of_words: useOfWords,
          thought_life: thoughtLife,
          use_of_time: useOfTime,
          use_of_money: useOfMoney,
          management_of_temptations: managementOfTemptations
        },
        reflection: {
          biggest_fear: biggestFear,
          biggest_temptation: biggestTemptation,
          biggest_victory: biggestVictory
        },
        was_honest: wasHonest,
        dishonesty_reason: wasHonest ? null : dishonestyReason,
        custom_responses: customResponsesArray,
      });

      navigate('/student', { state: { checkInSubmitted: true } });
    } catch (error) {
      console.error("Error submitting check-in:", error);
      alert(error.response?.data?.detail || "Failed to submit check-in. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const nextStep = () => {
    if (step < totalSteps) setStep(step + 1);
  };

  const prevStep = () => {
    if (step > 1) setStep(step - 1);
  };

  // Determine if current step is the final step (for submit button)
  const isFinalStep = step === totalSteps;
  // The honesty step is always step 5
  const isHonestyStep = step === 5;
  // Custom questions step is 6 (only if custom questions exist)
  const isCustomStep = hasCustomQuestions && step === 6;

  if (loading) {
    return (
      <Layout user={user}>
        <div className="flex items-center justify-center h-64">
          <div className="w-8 h-8 border-4 border-slate-300 border-t-amber-500 rounded-full animate-spin"></div>
        </div>
      </Layout>
    );
  }

  // Helper to get overridden field props
  const f = (key, defLabel, defDesc) => getFieldProps(key, fieldOverrides, defLabel, defDesc);

  return (
    <Layout user={user}>
      <div className="max-w-2xl mx-auto space-y-6">
        {/* Last check-in info banner */}
        {existingCheckIn && (
          <Alert className="bg-blue-50 border-blue-200">
            <CheckCircle2 className="h-5 w-5 text-blue-600" />
            <AlertTitle className="text-blue-800">Last Check-In</AlertTitle>
            <AlertDescription className="text-blue-700">
              You last checked in on {format(new Date(existingCheckIn.created_at), 'MMMM d, yyyy h:mm a')}. You can submit a new check-in anytime.
            </AlertDescription>
          </Alert>
        )}

        {/* Header */}
        <div className="text-center">
          <h1 className="font-serif text-2xl md:text-3xl font-bold text-slate-900">
            Weekly Check-In
          </h1>
          <p className="text-slate-600 mt-2">
            Reflect on your discipleship progress this week
          </p>
        </div>

        {/* Progress Steps */}
        <div className="flex items-center justify-center gap-2">
          {Array.from({ length: totalSteps }, (_, i) => i + 1).map((s) => (
            <div
              key={s}
              className={cn(
                "h-2 rounded-full transition-colors",
                totalSteps <= 5 ? "w-10" : "w-8",
                s === step ? "bg-amber-500" : s < step ? "bg-amber-300" : "bg-slate-200"
              )}
            />
          ))}
        </div>

        {/* Step 1: Date & Loved Well */}
        {step === 1 && (
          <Card>
            <CardHeader>
              <div className="flex items-center gap-2">
                <Heart className="w-5 h-5 text-red-500" />
                <CardTitle>Love Assessment</CardTitle>
              </div>
              <CardDescription>
                How well did you love God and others this week?
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Date Picker */}
              <div className="space-y-2">
                <Label>Check-in Date</Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      className={cn(
                        "w-full justify-start text-left font-normal",
                        !checkInDate && "text-muted-foreground"
                      )}
                    >
                      <CalendarIcon className="mr-2 h-4 w-4" />
                      {checkInDate ? format(checkInDate, "PPP") : "Select date"}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <Calendar
                      mode="single"
                      selected={checkInDate}
                      onSelect={setCheckInDate}
                      disabled={(date) => date > new Date()}
                      initialFocus
                    />
                  </PopoverContent>
                </Popover>
              </div>

              {/* Loved Well Ratings */}
              <div className="pt-4">
                <RatingSlider
                  label={f("loved_god", "I loved God well this week", "Through prayer, worship, and devotion").label}
                  value={lovedGod}
                  onChange={setLovedGod}
                  description={f("loved_god", "I loved God well this week", "Through prayer, worship, and devotion").description}
                />
                <RatingSlider
                  label={f("loved_people", "I loved people well this week", "Through kindness, service, and relationships").label}
                  value={lovedPeople}
                  onChange={setLovedPeople}
                  description={f("loved_people", "I loved people well this week", "Through kindness, service, and relationships").description}
                />
              </div>
            </CardContent>
          </Card>
        )}

        {/* Step 2: Fruits of the Spirit */}
        {step === 2 && (
          <Card>
            <CardHeader>
              <div className="flex items-center gap-2">
                <Sparkles className="w-5 h-5 text-amber-500" />
                <CardTitle>Fruits of the Spirit</CardTitle>
              </div>
              <CardDescription>
                Rate yourself on each fruit (Galatians 5:22-23)
              </CardDescription>
            </CardHeader>
            <CardContent>
              <RatingSlider label={f("love", "Love", "").label} value={love} onChange={setLove} description={f("love", "Love", "").description || undefined} />
              <RatingSlider label={f("joy", "Joy", "").label} value={joy} onChange={setJoy} description={f("joy", "Joy", "").description || undefined} />
              <RatingSlider label={f("peace", "Peace", "").label} value={peace} onChange={setPeace} description={f("peace", "Peace", "").description || undefined} />
              <RatingSlider label={f("patience", "Patience", "").label} value={patience} onChange={setPatience} description={f("patience", "Patience", "").description || undefined} />
              <RatingSlider label={f("kindness", "Kindness", "").label} value={kindness} onChange={setKindness} description={f("kindness", "Kindness", "").description || undefined} />
              <RatingSlider label={f("goodness", "Goodness", "").label} value={goodness} onChange={setGoodness} description={f("goodness", "Goodness", "").description || undefined} />
              <RatingSlider label={f("faithfulness", "Faithfulness", "").label} value={faithfulness} onChange={setFaithfulness} description={f("faithfulness", "Faithfulness", "").description || undefined} />
              <RatingSlider label={f("gentleness", "Gentleness", "").label} value={gentleness} onChange={setGentleness} description={f("gentleness", "Gentleness", "").description || undefined} />
              <RatingSlider label={f("self_control", "Self-Control", "").label} value={selfControl} onChange={setSelfControl} description={f("self_control", "Self-Control", "").description || undefined} />
            </CardContent>
          </Card>
        )}

        {/* Step 3: Glorifying God */}
        {step === 3 && (
          <Card>
            <CardHeader>
              <div className="flex items-center gap-2">
                <Target className="w-5 h-5 text-blue-500" />
                <CardTitle>Glorifying God</CardTitle>
              </div>
              <CardDescription>
                How well did you honor God in these areas?
              </CardDescription>
            </CardHeader>
            <CardContent>
              <RatingSlider 
                label={f("use_of_words", "My use of words", "Speaking truth, encouragement, and avoiding gossip").label}
                value={useOfWords} 
                onChange={setUseOfWords}
                description={f("use_of_words", "My use of words", "Speaking truth, encouragement, and avoiding gossip").description}
              />
              <RatingSlider 
                label={f("thought_life", "My thought life", "Keeping pure and positive thoughts").label}
                value={thoughtLife} 
                onChange={setThoughtLife}
                description={f("thought_life", "My thought life", "Keeping pure and positive thoughts").description}
              />
              <RatingSlider 
                label={f("use_of_time", "My use of time", "Being productive and prioritizing what matters").label}
                value={useOfTime} 
                onChange={setUseOfTime}
                description={f("use_of_time", "My use of time", "Being productive and prioritizing what matters").description}
              />
              <RatingSlider 
                label={f("use_of_money", "My use of money", "Being generous and responsible").label}
                value={useOfMoney} 
                onChange={setUseOfMoney}
                description={f("use_of_money", "My use of money", "Being generous and responsible").description}
              />
              <RatingSlider 
                label={f("management_of_temptations", "My management of temptations", "Resisting and overcoming temptation").label}
                value={managementOfTemptations} 
                onChange={setManagementOfTemptations}
                description={f("management_of_temptations", "My management of temptations", "Resisting and overcoming temptation").description}
              />
            </CardContent>
          </Card>
        )}

        {/* Step 4: Reflection Questions */}
        {step === 4 && (
          <Card>
            <CardHeader>
              <div className="flex items-center gap-2">
                <MessageSquare className="w-5 h-5 text-purple-500" />
                <CardTitle>Reflection</CardTitle>
              </div>
              <CardDescription>
                Take a moment to reflect honestly
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="space-y-2">
                <Label htmlFor="fear">{f("biggest_fear", "What is your biggest fear?", "Be honest with yourself...").label}</Label>
                <Textarea
                  id="fear"
                  placeholder={f("biggest_fear", "What is your biggest fear?", "Be honest with yourself...").description}
                  value={biggestFear}
                  onChange={(e) => setBiggestFear(e.target.value)}
                  className="min-h-[100px]"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="temptation">{f("biggest_temptation", "What is your biggest temptation?", "What do you struggle with most?").label}</Label>
                <Textarea
                  id="temptation"
                  placeholder={f("biggest_temptation", "What is your biggest temptation?", "What do you struggle with most?").description}
                  value={biggestTemptation}
                  onChange={(e) => setBiggestTemptation(e.target.value)}
                  className="min-h-[100px]"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="victory">{f("biggest_victory", "What is your biggest victory?", "Celebrate your wins!").label}</Label>
                <Textarea
                  id="victory"
                  placeholder={f("biggest_victory", "What is your biggest victory?", "Celebrate your wins!").description}
                  value={biggestVictory}
                  onChange={(e) => setBiggestVictory(e.target.value)}
                  className="min-h-[100px]"
                />
              </div>
            </CardContent>
          </Card>
        )}

        {/* Step 5: Honesty Check */}
        {step === 5 && (
          <Card>
            <CardHeader>
              <div className="flex items-center gap-2">
                <CheckCircle2 className="w-5 h-5 text-green-500" />
                <CardTitle>Honesty Check</CardTitle>
              </div>
              <CardDescription>
                {isFinalStep ? "Final step" : "Almost done"} - be honest with yourself and God
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="space-y-4">
                <Label className="text-base font-medium">
                  {f("was_honest", "Were you fully honest in filling this out?", "").label}
                </Label>
                <RadioGroup
                  value={wasHonest === null ? undefined : wasHonest ? "yes" : "no"}
                  onValueChange={(val) => setWasHonest(val === "yes")}
                  className="space-y-3"
                >
                  <div className="flex items-center space-x-3 p-4 rounded-lg border border-slate-200 hover:bg-slate-50 cursor-pointer">
                    <RadioGroupItem value="yes" id="yes" />
                    <Label htmlFor="yes" className="cursor-pointer flex-1">
                      Yes, I was fully honest
                    </Label>
                  </div>
                  <div className="flex items-center space-x-3 p-4 rounded-lg border border-slate-200 hover:bg-slate-50 cursor-pointer">
                    <RadioGroupItem value="no" id="no" />
                    <Label htmlFor="no" className="cursor-pointer flex-1">
                      No, I was not fully honest
                    </Label>
                  </div>
                </RadioGroup>
              </div>

              {wasHonest === false && (
                <div className="space-y-2 animate-in fade-in slide-in-from-top-2">
                  <Label htmlFor="dishonesty">
                    If no, why are you being dishonest with yourself and God?
                  </Label>
                  <Textarea
                    id="dishonesty"
                    placeholder="Take a moment to reflect on this..."
                    value={dishonestyReason}
                    onChange={(e) => setDishonestyReason(e.target.value)}
                    className="min-h-[120px]"
                  />
                  <Alert className="bg-amber-50 border-amber-200">
                    <AlertCircle className="h-4 w-4 text-amber-600" />
                    <AlertDescription className="text-amber-800 text-sm">
                      It takes courage to acknowledge this. God already knows your heart, 
                      and honest self-reflection is the first step toward growth.
                    </AlertDescription>
                  </Alert>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Step 6: Custom Questions (only if assigned) */}
        {isCustomStep && (
          <Card>
            <CardHeader>
              <div className="flex items-center gap-2">
                <ClipboardList className="w-5 h-5 text-indigo-500" />
                <CardTitle>Additional Questions</CardTitle>
              </div>
              <CardDescription>
                Final step - your teacher has a few additional questions for you
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {customQuestions.map((q) => (
                <CustomQuestionField
                  key={q.id}
                  question={q}
                  value={customResponses[q.id]}
                  onChange={(val) =>
                    setCustomResponses((prev) => ({ ...prev, [q.id]: val }))
                  }
                />
              ))}
            </CardContent>
          </Card>
        )}

        {/* Navigation Buttons */}
        <div className="flex gap-4">
          {step > 1 && (
            <Button
              variant="outline"
              onClick={prevStep}
              className="flex-1"
            >
              Previous
            </Button>
          )}
          
          {!isFinalStep ? (
            <Button
              onClick={nextStep}
              className="flex-1 bg-amber-600 hover:bg-amber-700"
            >
              Continue
              <ChevronRight className="w-4 h-4 ml-1" />
            </Button>
          ) : (
            <Button
              onClick={handleSubmit}
              disabled={submitting || wasHonest === null}
              className="flex-1 bg-green-600 hover:bg-green-700"
            >
              {submitting ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Submitting...
                </>
              ) : (
                <>
                  <CheckCircle2 className="w-4 h-4 mr-2" />
                  Submit Check-In
                </>
              )}
            </Button>
          )}
        </div>
      </div>
    </Layout>
  );
}
